import { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "@/lib/apiClient";

export type OperationRegion = "domestic" | "international";

interface OperationRegionResponse {
  operation_region: OperationRegion;
  default_language: string | null;
}

const CACHE_KEY = "operation_region_cache";
const CACHE_EXPIRY_KEY = "operation_region_cache_expiry";
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5分钟缓存

// 模块级别的请求状态，防止多个组件实例同时发起请求
let pendingRequest: Promise<OperationRegionResponse> | null = null;

/**
 * 从 localStorage 读取缓存
 */
function getCachedData(): OperationRegionResponse | null {
  try {
    const cachedData = localStorage.getItem(CACHE_KEY);
    const cachedExpiry = localStorage.getItem(CACHE_EXPIRY_KEY);

    if (!cachedData || !cachedExpiry) {
      return null;
    }

    const expiryTime = parseInt(cachedExpiry, 10);
    if (Date.now() > expiryTime) {
      // 缓存已过期，清除
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_EXPIRY_KEY);
      return null;
    }

    return JSON.parse(cachedData) as OperationRegionResponse;
  } catch (e) {
    console.warn("Failed to read operation region cache:", e);
    return null;
  }
}

/**
 * 保存数据到 localStorage 缓存
 */
function setCachedData(data: OperationRegionResponse): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(
      CACHE_EXPIRY_KEY,
      String(Date.now() + CACHE_DURATION_MS)
    );
  } catch (e) {
    console.warn("Failed to save operation region cache:", e);
  }
}

/**
 * 清除缓存
 */
function clearCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_EXPIRY_KEY);
  } catch (e) {
    console.warn("Failed to clear operation region cache:", e);
  }
}

/**
 * 从 API 获取运营区域配置（带请求去重）
 */
async function fetchFromAPI(): Promise<OperationRegionResponse> {
  // 如果已有进行中的请求，复用它
  if (pendingRequest) {
    return pendingRequest;
  }

  pendingRequest = apiClient<OperationRegionResponse>(
    "/api/v1/site_settings/operation_region",
    { disableAutoToast: true }
  );

  try {
    const response = await pendingRequest;
    return response;
  } finally {
    pendingRequest = null;
  }
}

/**
 * 运营区域 Hook
 *
 * 获取系统配置的运营区域，决定显示不同的登录渠道：
 * - 'domestic': 国内模式 -> 显示手机号登录、微信登录，固定中文
 * - 'international': 海外模式 -> 显示邮箱登录、Google登录、Apple登录，支持多语言
 *
 * 特性：
 * 1. 从 localStorage 读取缓存，避免重复请求
 * 2. 缓存过期后自动刷新
 * 3. 提供 refetch 方法手动刷新缓存
 * 4. 多组件实例共享同一请求，避免重复调用
 *
 * @returns {object} { region, defaultLanguage, loading, error, isDomestic, isInternational, refetch }
 */
export function useOperationRegion() {
  const [region, setRegion] = useState<OperationRegion>(() => {
    // 初始化时尝试从缓存读取
    const cached = getCachedData();
    return cached?.operation_region ?? "international";
  });
  const [defaultLanguage, setDefaultLanguage] = useState<string | null>(() => {
    const cached = getCachedData();
    return cached?.default_language ?? null;
  });
  const [loading, setLoading] = useState(() => {
    // 如果有缓存，初始就不是 loading 状态
    return getCachedData() === null;
  });
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  /**
   * 加载运营区域配置
   */
  const loadOperationRegion = useCallback(async (forceRefresh = false) => {
    // 如果不是强制刷新，先尝试读取缓存
    if (!forceRefresh) {
      const cached = getCachedData();
      if (cached) {
        setRegion(cached.operation_region);
        setDefaultLanguage(cached.default_language);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchFromAPI();
      if (mountedRef.current) {
        setRegion(data.operation_region);
        setDefaultLanguage(data.default_language);
        setCachedData(data);
      }
    } catch (err) {
      console.error("Failed to fetch operation region:", err);
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : "Unknown error");
        // 发生错误时使用默认值
        setRegion("international");
        setDefaultLanguage(null);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * 手动刷新缓存
   */
  const refetch = useCallback(() => {
    clearCache();
    return loadOperationRegion(true);
  }, [loadOperationRegion]);

  // 初始化加载 - 只在挂载时执行一次
  useEffect(() => {
    mountedRef.current = true;
    loadOperationRegion();

    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    region,
    defaultLanguage,
    loading,
    error,
    isDomestic: region === "domestic",
    isInternational: region === "international",
    refetch,
  };
}
